import React, { useEffect, useState } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { helpArticleService } from '../../services/helpArticleService';
import { HelpArticle, HelpArticleFormData, HELP_CATEGORIES } from '../../types/helpArticle';

const AdminHelpCenterManager: React.FC = () => {
  const { showError, showSuccess } = useNotification();
  const { currentUser } = useAuth();
  const { isDark } = useAdminTheme();
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<HelpArticle | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState<HelpArticleFormData>({
    title: '',
    content: '',
    category: 'getting-started',
    url: '',
    slug: '',
    order: 0,
    isActive: true,
    isFeatured: false,
    tags: [],
  });
  const [tagInput, setTagInput] = useState('');

  const isFormValid = !!form.title.trim() && !!form.content.trim() && !!form.slug.trim();

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    // Auto-generate slug from title
    if (form.title && !editing) {
      const generatedSlug = helpArticleService.generateSlug(form.title);
      setForm({ ...form, slug: generatedSlug });
    }
  }, [form.title, editing]);

  const load = async () => {
    try {
      setLoading(true);
      const data = await helpArticleService.getAllArticles(true);
      setArticles(data);
    } catch (e) {
      console.error(e);
      showError('Failed to load help articles');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({
      title: '',
      content: '',
      category: 'getting-started',
      url: '',
      slug: '',
      order: articles.length,
      isActive: true,
      isFeatured: false,
      tags: [],
    });
    setTagInput('');
    setShowModal(true);
  };

  const openEdit = (article: HelpArticle) => {
    setEditing(article);
    setForm({
      title: article.title,
      content: article.content,
      category: article.category,
      url: article.url || '',
      slug: article.slug,
      order: article.order,
      isActive: article.isActive,
      isFeatured: article.isFeatured,
      tags: article.tags || [],
    });
    setTagInput('');
    setShowModal(true);
  };

  const submit = async () => {
    try {
      if (!isFormValid) {
        showError('Please fill all required fields');
        return;
      }
      if (!currentUser?.uid) {
        showError('User not authenticated');
        return;
      }

      setIsSubmitting(true);
      if (editing) {
        await helpArticleService.updateArticle(editing.id, form, currentUser.uid);
        showSuccess('Help article updated');
      } else {
        await helpArticleService.createArticle(form, currentUser.uid);
        showSuccess('Help article created');
      }
      setShowModal(false);
      await load();
    } catch (e: any) {
      console.error(e);
      showError(e.message || 'Failed to save');
    } finally {
      setIsSubmitting(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this help article?')) return;
    try {
      await helpArticleService.deleteArticle(id);
      showSuccess('Deleted');
      await load();
    } catch (e: any) {
      console.error(e);
      showError(e.message || 'Failed to delete');
    }
  };

  const toggleActive = async (article: HelpArticle) => {
    try {
      if (!currentUser?.uid) return;
      await helpArticleService.updateArticle(article.id, { isActive: !article.isActive }, currentUser.uid);
      await load();
    } catch (e: any) {
      showError(e.message || 'Failed to update status');
    }
  };

  const toggleFeatured = async (article: HelpArticle) => {
    try {
      if (!currentUser?.uid) return;
      await helpArticleService.updateArticle(article.id, { isFeatured: !article.isFeatured }, currentUser.uid);
      await load();
    } catch (e: any) {
      showError(e.message || 'Failed to update featured status');
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !form.tags.includes(tag)) {
      setForm({ ...form, tags: [...form.tags, tag] });
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setForm({ ...form, tags: form.tags.filter((t) => t !== tag) });
  };

  const filteredArticles = articles.filter((article) => {
    const matchesCategory = filterCategory === 'all' || article.category === filterCategory;
    const matchesSearch =
      !searchTerm ||
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const getCategoryName = (categoryId: string) => {
    return HELP_CATEGORIES.find((c) => c.id === categoryId)?.name || categoryId;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: isDark ? '#f9fafb' : '#030712' }}>Help Center Manager</h1>
          <p className="text-sm font-medium" style={{ color: isDark ? '#e5e7eb' : '#111827' }}>
            Manage help articles and support topics for the help center
          </p>
        </div>
        <button
          onClick={openAdd}
          className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg hover:opacity-80 transition-opacity"
        >
          Add Article
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All Categories</option>
          {HELP_CATEGORIES.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-700 dark:text-gray-300">Loading...</div>
      ) : filteredArticles.length === 0 ? (
        <div className="py-12 text-center text-gray-700 dark:text-gray-300">
          {searchTerm || filterCategory !== 'all' ? 'No articles found matching your filters' : 'No articles yet'}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredArticles.map((article) => (
            <div
              key={article.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{article.title}</h3>
                    <span className="px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                      {getCategoryName(article.category)}
                    </span>
                    {article.isFeatured && (
                      <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300">
                        Featured
                      </span>
                    )}
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        article.isActive
                          ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {article.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-2 line-clamp-2">{article.content}</p>
                  {article.url && (
                    <a
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-700 dark:text-blue-300 hover:underline font-medium"
                    >
                      {article.url}
                    </a>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-xs text-gray-800 dark:text-gray-200">
                    <span className="text-gray-800 dark:text-gray-200">Order: {article.order}</span>
                    <span className="text-gray-800 dark:text-gray-200">Views: {article.views || 0}</span>
                    <span className="text-gray-800 dark:text-gray-200">Helpful: {article.helpfulCount || 0}</span>
                    {article.tags.length > 0 && (
                      <div className="flex items-center gap-1">
                        <span className="text-gray-800 dark:text-gray-200">Tags:</span>
                        {article.tags.map((tag) => (
                          <span key={tag} className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-2 ml-4">
                  <button
                    onClick={() => openEdit(article)}
                    className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => toggleActive(article)}
                    className={`px-3 py-2 text-sm rounded ${
                      article.isActive
                        ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                        : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                    }`}
                  >
                    {article.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => toggleFeatured(article)}
                    className={`px-3 py-2 text-sm rounded ${
                      article.isFeatured
                        ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                    }`}
                  >
                    {article.isFeatured ? 'Unfeature' : 'Feature'}
                  </button>
                  <button
                    onClick={() => remove(article.id)}
                    className="px-3 py-2 text-sm bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-800"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl my-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {editing ? 'Edit Help Article' : 'Add Help Article'}
            </h2>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                  Title *
                </label>
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="How to create your first campaign"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                  Slug * (auto-generated from title)
                </label>
                <input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="how-to-create-your-first-campaign"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                  Category *
                </label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {HELP_CATEGORIES.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                  Content * (Markdown supported)
                </label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                  placeholder="Write detailed help content here. You can use Markdown formatting..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                  URL (optional - for external links)
                </label>
                <input
                  type="url"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://example.com/article"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">Tags</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Add tag and press Enter"
                  />
                  <button
                    onClick={addTag}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Add
                  </button>
                </div>
                {form.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {form.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-sm flex items-center gap-1"
                      >
                        {tag}
                        <button
                          onClick={() => removeTag(tag)}
                          className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-200"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">Order</label>
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || '0', 10) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    />
                    Active
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                    <input
                      type="checkbox"
                      checked={form.isFeatured}
                      onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })}
                    />
                    Featured (show in suggested questions)
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={!isFormValid || isSubmitting}
                className={`px-4 py-2 rounded ${
                  !isFormValid || isSubmitting
                    ? 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-black dark:bg-white text-white dark:text-black hover:opacity-80'
                }`}
              >
                {isSubmitting ? (editing ? 'Updating...' : 'Creating...') : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHelpCenterManager;

